import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetUserQuery, useUpdateUserVerificationMutation, useGetAdsQuery, useAddWalletBalanceMutation, useGetUserViolationsQuery, useGiveUserViolationMutation, useGetUserReportsQuery } from '../../../store/api/adminApi';
import PageLoader from '../../../components/PageLoader';
import Image from '../../../components/Image';
import { formatDateOnly, formatDateTime, getStatusColor, formatCurrency } from '../../../utils/helpers';
import { showSuccess, showError } from '../../../utils/toast';
import ActionButtons from '../../../components/ActionButtons';


const AdminUserView: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState<'info' | 'ads' | 'violations' | 'reports'>('info');

  // Verification modal
  const [modalOpen, setModalOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<'approve' | 'reject' | null>(null);
  const [node, setNode] = useState('');
  const [documents, setDocuments] = useState<File[]>([]);

  // Wallet modal
  const [walletModalOpen, setWalletModalOpen] = useState(false);
  const [walletType, setWalletType] = useState<'add' | 'deduct'>('add');
  const [walletAmount, setWalletAmount] = useState('');
  const [walletNote, setWalletNote] = useState('');

  // Violation modal
  const [violationModalOpen, setViolationModalOpen] = useState(false);
  const [violationAdId, setViolationAdId] = useState('');
  const [violationType, setViolationType] = useState('Policy Non-Compliance');
  const [violationStatus, setViolationStatus] = useState('Under Review');
  const [violationNote, setViolationNote] = useState('');

  const { data, isLoading, error } = useGetUserQuery(id!);
  const { data: adsData, isLoading: isAdsLoading } = useGetAdsQuery({ user_id: id }, { skip: !id || (activeTab !== 'ads' && !violationModalOpen) });
  const { data: violationsData, isLoading: isViolationsLoading } = useGetUserViolationsQuery(id!, { skip: !id || activeTab !== 'violations' });
  const { data: reportsData, isLoading: isReportsLoading } = useGetUserReportsQuery(id!, { skip: !id || activeTab !== 'reports' });
  const [giveUserViolation, { isLoading: isViolationSubmitting }] = useGiveUserViolationMutation();
  const [updateUserVerification, { isLoading: isUpdating }] = useUpdateUserVerificationMutation();
  const [addWalletBalance, { isLoading: isWalletLoading }] = useAddWalletBalanceMutation();

  const openWalletModal = (type: 'add' | 'deduct') => {
    setWalletType(type);
    setWalletAmount('');
    setWalletNote('');
    setWalletModalOpen(true);
  };

  const handleWalletSubmit = async () => {
    const amount = parseFloat(walletAmount);
    if (!amount || amount <= 0) return showError('Please enter a valid amount.');
    try {
      const result = await addWalletBalance({
        id: user.id,
        amount,
        type: walletType,
        note: walletNote || undefined,
      }).unwrap();
      showSuccess(result.message || 'Wallet balance updated!');
      setWalletModalOpen(false);
    } catch (err: any) {
      showError(err?.data?.message || 'Failed to update wallet.');
    }
  };

  const openViolationModal = () => {
    setViolationAdId('');
    setViolationType('Policy Non-Compliance');
    setViolationStatus('Under Review');
    setViolationNote('');
    setViolationModalOpen(true);
  };

  const handleViolationSubmit = async () => {
    if (!violationAdId) return showError('Please select an ad.');
    try {
      const result = await giveUserViolation({
        userId: user.id,
        ad_id: violationAdId,
        violation_type: violationType,
        status: violationStatus,
        note: violationNote || undefined,
      }).unwrap();
      showSuccess(result.message || 'Violation issued successfully!');
      setViolationModalOpen(false);
    } catch (err: any) {
      showError(err?.data?.message || 'Failed to issue violation.');
    }
  };

  const openVerificationModal = (status: 'approve' | 'reject') => {
    setPendingStatus(status);
    setNode('');
    setDocuments([]);
    setModalOpen(true);
  };

  const handleVerificationSubmit = async () => {
    if (!pendingStatus) return;
    try {
      const result = await updateUserVerification({
        id: user.id,
        status: pendingStatus,
        node,
        documents,
      }).unwrap();
      showSuccess(result.message || `User verification ${pendingStatus}d successfully!`);
      setModalOpen(false);
    } catch (err: any) {
      showError(err?.data?.message || 'Failed to update verification status.');
    }
  };

  if (isLoading) {
    return <PageLoader />;
  }

  if (error || !data?.data) {
    return <div className="alert alert-danger m-5">User not found</div>;
  }

  const user = data.data;

  return (
    <>
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">User Details</h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">
                  <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/dashboard'); }} className="text-muted text-hover-primary">Home</a>
                </li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-400 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">
                  <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/users'); }} className="text-muted text-hover-primary">Users</a>
                </li>
              </ul>
            </div>
            <div className="d-flex align-items-center gap-2 gap-lg-3">
              <button className="btn btn-primary" onClick={() => navigate(`/admin/users/${id}/edit`)}>
                Edit User
              </button>
            </div>
          </div>
        </div>
        
        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-xxl">
            
            {/* User Header Card */}
            <div className="card mb-5 mb-xl-10">
              <div className="card-body pt-9 pb-0">
                <div className="d-flex flex-wrap flex-sm-nowrap">
                  <div className="me-7 mb-4">
                    <div className="symbol symbol-100px symbol-lg-160px symbol-fixed position-relative">
                      {user.picture ? (
                        <Image 
                          src={user.picture} 
                          alt={user.name} 
                          type="avatar"
                          className="w-100px h-100px rounded-3 object-fit-cover"
                        />
                      ) : (
                        <div className="symbol-label fs-1 fw-bold bg-light-primary text-primary">
                          {user.name.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div className={`position-absolute translate-middle bottom-0 start-100 mb-6 bg-${user.accStatus === 1 ? 'success' : 'danger'} rounded-circle border border-4 border-body h-20px w-20px`}></div>
                    </div>
                  </div>

                  <div className="flex-grow-1">
                    <div className="d-flex justify-content-between align-items-start flex-wrap mb-2">
                      <div className="d-flex flex-column">
                        <div className="d-flex align-items-center mb-2">
                          <span className="text-gray-900 text-hover-primary fs-2 fw-bold me-1">{user.name}</span>
                          {user.verification_badge === 'Green' && (
                             <span className="svg-icon svg-icon-1 svg-icon-primary">
                               <svg xmlns="http://www.w3.org/2000/svg" width="24px" height="24px" viewBox="0 0 24 24">
                                 <path d="M10.0813 3.7242C10.8849 2.16438 13.1151 2.16438 13.9187 3.7242V3.7242C14.4016 4.66147 15.4909 5.1127 16.4951 4.79139V4.79139C18.1663 4.25668 19.7433 5.83365 19.2086 7.50485V7.50485C18.8873 8.50905 19.3385 9.59842 20.2758 10.0813V10.0813C21.8356 10.8849 21.8356 13.1151 20.2758 13.9187V13.9187C19.3385 14.4016 18.8873 15.491 19.2086 16.4951V16.4951C19.7433 18.1663 18.1663 19.7433 16.4951 19.2086V19.2086C15.491 18.8873 14.4016 19.3385 13.9187 20.2758V20.2758C13.1151 21.8356 10.8849 21.8356 10.0813 20.2758V20.2758C9.59842 19.3385 8.50905 18.8873 7.50485 19.2086V19.2086C5.83365 19.7433 4.25668 18.1663 4.79139 16.4951V16.4951C5.1127 15.491 4.66147 14.4016 3.7242 13.9187V13.9187C2.16438 13.1151 2.16438 10.8849 3.7242 10.0813V10.0813C4.66147 9.59842 5.1127 8.50905 4.79139 7.50485V7.50485C4.25668 5.83365 5.83365 4.25668 7.50485 4.79139V4.79139C8.50905 5.1127 9.59842 4.66147 10.0813 3.7242V3.7242Z" fill="currentColor"/>
                                 <path d="M14.8563 9.1903C15.0606 8.94984 15.3771 8.9385 15.6175 9.14289C15.858 9.34728 15.8229 9.66433 15.6185 9.9048L11.863 14.6558C11.6554 14.9001 11.2876 14.9258 11.048 14.7128L8.47656 12.4271C8.24068 12.2174 8.21944 11.8563 8.42911 11.6204C8.63877 11.3845 8.99996 11.3633 9.23583 11.5729L11.3706 13.4705L14.8563 9.1903Z" fill="white"/>
                               </svg>
                             </span>
                          )}
                          {user.verification_badge === 'Gold' && (
                             <span className="svg-icon svg-icon-1 svg-icon-warning">
                               <svg xmlns="http://www.w3.org/2000/svg" width="24px" height="24px" viewBox="0 0 24 24">
                                 <path d="M10.0813 3.7242C10.8849 2.16438 13.1151 2.16438 13.9187 3.7242V3.7242C14.4016 4.66147 15.4909 5.1127 16.4951 4.79139V4.79139C18.1663 4.25668 19.7433 5.83365 19.2086 7.50485V7.50485C18.8873 8.50905 19.3385 9.59842 20.2758 10.0813V10.0813C21.8356 10.8849 21.8356 13.1151 20.2758 13.9187V13.9187C19.3385 14.4016 18.8873 15.491 19.2086 16.4951V16.4951C19.7433 18.1663 18.1663 19.7433 16.4951 19.2086V19.2086C15.491 18.8873 14.4016 19.3385 13.9187 20.2758V20.2758C13.1151 21.8356 10.8849 21.8356 10.0813 20.2758V20.2758C9.59842 19.3385 8.50905 18.8873 7.50485 19.2086V19.2086C5.83365 19.7433 4.25668 18.1663 4.79139 16.4951V16.4951C5.1127 15.491 4.66147 14.4016 3.7242 13.9187V13.9187C2.16438 13.1151 2.16438 10.8849 3.7242 10.0813V10.0813C4.66147 9.59842 5.1127 8.50905 4.79139 7.50485V7.50485C4.25668 5.83365 5.83365 4.25668 7.50485 4.79139V4.79139C8.50905 5.1127 9.59842 4.66147 10.0813 3.7242V3.7242Z" fill="currentColor"/>
                                 <path d="M14.8563 9.1903C15.0606 8.94984 15.3771 8.9385 15.6175 9.14289C15.858 9.34728 15.8229 9.66433 15.6185 9.9048L11.863 14.6558C11.6554 14.9001 11.2876 14.9258 11.048 14.7128L8.47656 12.4271C8.24068 12.2174 8.21944 11.8563 8.42911 11.6204C8.63877 11.3845 8.99996 11.3633 9.23583 11.5729L11.3706 13.4705L14.8563 9.1903Z" fill="white"/>
                               </svg>
                             </span>
                          )}
                        </div>

                        <div className="d-flex flex-wrap fw-semibold fs-6 mb-4 pe-2">
                          <span className="d-flex align-items-center text-gray-400 text-hover-primary me-5 mb-2">
                            <i className="bi bi-envelope-fill fs-4 me-1"></i>
                            {user.email}
                          </span>
                          <span className="d-flex align-items-center text-gray-400 text-hover-primary me-5 mb-2">
                            <i className="bi bi-geo-alt-fill fs-4 me-1"></i>
                            {user.city}, {user.country}
                          </span>
                          <span className="d-flex align-items-center text-gray-400 text-hover-primary mb-2">
                            <i className="bi bi-phone-fill fs-4 me-1"></i>
                            {user.phone || 'N/A'}
                          </span>
                        </div>
                      </div>

                      <div className="d-flex flex-column gap-3 my-4">
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          {user.verification_badge?.startsWith('Rejected') ? (
                            <div className="badge badge-light-danger fs-7 fw-bold">
                              {user.verification_badge || 'Unverified'}
                            </div>
                          ) : (
                            <div className={`badge badge-light-${user.verification_badge?.startsWith('Requested') ? 'warning' : (user.verification_badge !== 'Unverified' ? 'success' : 'secondary text-dark')} fs-7 fw-bold`}>
                              {user.verification_badge || 'Unverified'}
                            </div>
                          )}

                          {user.verification_badge?.startsWith('Requested') && (
                            <div className="d-flex gap-2">
                              <button
                                className="btn btn-sm btn-success"
                                onClick={() => openVerificationModal('approve')}
                              >
                                Approve
                              </button>
                              <button
                                className="btn btn-sm btn-danger"
                                onClick={() => openVerificationModal('reject')}
                              >
                                Reject
                              </button>
                            </div>
                          )}
                        </div>

                        {/* Badge note */}
                        {user.badge_node && (
                          <div className="d-flex align-items-start gap-2">
                            <i className="bi bi-chat-left-text-fill text-muted mt-1"></i>
                            <div>
                              <div className="fw-semibold text-gray-700 fs-7 mb-1">Admin Note</div>
                              <span className="text-gray-600 fs-7">{user.badge_node}</span>
                            </div>
                          </div>
                        )}

                        {/* Badge documents */}
                        {user.badge_documents && (() => {
                          const docs: string[] = typeof user.badge_documents === 'string'
                            ? JSON.parse(user.badge_documents)
                            : user.badge_documents;
                          return docs.length > 0 ? (
                            <div className="d-flex align-items-start gap-2">
                              <i className="bi bi-paperclip text-muted mt-1"></i>
                              <div>
                                <div className="fw-semibold text-gray-700 fs-7 mb-1">Documents</div>
                                <div className="d-flex flex-wrap gap-2">
                                  {docs.map((doc: string, idx: number) => {
                                    const fileName = doc.split('/').pop() || doc;
                                    const ext = fileName.split('.').pop()?.toLowerCase();
                                    const isImage = ['jpg', 'jpeg', 'png'].includes(ext || '');
                                    
                                    return (
                                      <a
                                        key={idx}
                                        href={doc}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="btn btn-sm btn-light-primary py-1 px-3 fs-7"
                                      >
                                        <i className={`bi ${isImage ? 'bi-image' : 'bi-file-earmark'} me-1`}></i>
                                        {fileName}
                                      </a>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                          ) : null;
                        })()}
                      </div>
                    </div>
                  </div>
                </div>

                <ul className="nav nav-tabs nav-pills border-0 flex-row d-flex fs-6 fw-semibold mt-3 gap-2">
                  {([
                    { key: 'info', label: 'User Info', icon: 'bi-person-lines-fill' },
                    { key: 'ads', label: 'User Ads', icon: 'bi-megaphone-fill' },
                    { key: 'violations', label: 'Violations', icon: 'bi-exclamation-triangle-fill' },
                    { key: 'reports', label: 'Reports', icon: 'bi-flag-fill' },
                  ] as const).map((tab) => (
                    <li className="nav-item" key={tab.key}>
                      <button
                        className={`nav-link d-flex align-items-center px-5 py-3 rounded-pill border ${
                          activeTab === tab.key
                            ? 'active bg-primary text-white border-primary'
                            : 'bg-light text-gray-700 border-gray-200'
                        }`}
                        onClick={() => setActiveTab(tab.key)}
                      >
                        <i className={`bi ${tab.icon} fs-5 me-2 ${activeTab === tab.key ? 'text-white' : 'text-primary'}`}></i>
                        {tab.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Tab Content */}
            {activeTab === 'info' && (
              <div className="card mb-5 mb-xl-10">
                <div className="card-header cursor-pointer">
                  <div className="card-title m-0">
                    <h3 className="fw-bold m-0">Profile Details</h3>
                  </div>
                </div>
                <div className="card-body p-9">
                  <div className="row mb-7">
                    <label className="col-lg-4 fw-semibold text-muted">Full Name</label>
                    <div className="col-lg-8">
                      <span className="fw-bold fs-6 text-gray-800">{user.name}</span>
                    </div>
                  </div>
                  <div className="row mb-7">
                    <label className="col-lg-4 fw-semibold text-muted">Username</label>
                    <div className="col-lg-8">
                      {user.username ? (
                        <span className="fw-bold fs-6 text-gray-800">@{user.username}</span>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </div>
                  </div>
                  <div className="row mb-7">
                    <label className="col-lg-4 fw-semibold text-muted">Email</label>
                    <div className="col-lg-8">
                      <span className="fw-bold fs-6 text-gray-800">{user.email}</span>
                    </div>
                  </div>
                  <div className="row mb-7">
                    <label className="col-lg-4 fw-semibold text-muted">Contact Phone</label>
                    <div className="col-lg-8">
                      <span className="fw-bold fs-6 text-gray-800">{user.phone || 'N/A'}</span>
                    </div>
                  </div>
                 
                  <div className="row mb-7">
                    <label className="col-lg-4 fw-semibold text-muted">Location</label>
                    <div className="col-lg-8">
                      <span className="fw-bold fs-6 text-gray-800">{user.city || '--'}, {user.country || '--'}</span>
                    </div>
                  </div>
                  <div className="row mb-7">
                    <label className="col-lg-4 fw-semibold text-muted">Account Status</label>
                    <div className="col-lg-8">
                      <span className={`badge badge-light-${user.accStatus === 1 ? 'success' : 'danger'}`}>
                        {user.accStatus === 1 ? 'Active' : 'Suspended'}
                      </span>
                    </div>
                  </div>
                  <div className="row mb-7">
                    <label className="col-lg-4 fw-semibold text-muted">Classification</label>
                    <div className="col-lg-8">
                      <span className="badge badge-light-info fs-7 fw-bold text-capitalize">
                        {user.user_type ? user.user_type.replace(/_/g, ' ') : 'N/A'}
                      </span>
                    </div>
                  </div>
                  <div className="row mb-7">
                    <label className="col-lg-4 fw-semibold text-muted">Signin Method</label>
                    <div className="col-lg-8">
                      {user.googleId ? (
                        <span className="badge badge-light-danger fs-7 fw-bold"><i className="bi bi-google me-1"></i>Google</span>
                      ) : user.appleId ? (
                        <span className="badge badge-light-dark fs-7 fw-bold"><i className="bi bi-apple me-1"></i>Apple</span>
                      ) : (
                        <span className="badge badge-light-primary fs-7 fw-bold"><i className="bi bi-envelope-fill me-1"></i>Email</span>
                      )}
                    </div>
                  </div>
                  <div className="row mb-7">
                    <label className="col-lg-4 fw-semibold text-muted">Verification</label>
                    <div className="col-lg-8">
                      <span className={`badge fs-7 fw-bold ${
                        user.verification_badge?.includes('Green') ? 'badge-light-success' :
                        user.verification_badge?.includes('Gold') ? 'badge-light-warning' :
                        user.verification_badge?.startsWith('Requested') ? 'badge-light-warning' :
                        user.verification_badge?.startsWith('Rejected') ? 'badge-light-danger' :
                        'badge-light-secondary text-dark'
                      }`}>
                        {user.verification_badge || 'Unverified'}
                      </span>
                    </div>
                  </div>
                  {user.badge_node && (
                    <div className="row mb-7">
                      <label className="col-lg-4 fw-semibold text-muted">Badge Note</label>
                      <div className="col-lg-8">
                        <span className="fw-bold fs-6 text-gray-800">{user.badge_node}</span>
                      </div>
                    </div>
                  )}
                  {user.badge_documents && (() => {
                    const docs: string[] = typeof user.badge_documents === 'string'
                      ? JSON.parse(user.badge_documents)
                      : user.badge_documents;
                    return docs.length > 0 ? (
                      <div className="row mb-7">
                        <label className="col-lg-4 fw-semibold text-muted">Badge Documents</label>
                        <div className="col-lg-8 d-flex flex-wrap gap-2">
                          {docs.map((doc: string, idx: number) => {
                            const fileName = doc.split('/').pop() || doc;
                            const ext = fileName.split('.').pop()?.toLowerCase();
                            const isImage = ['jpg', 'jpeg', 'png'].includes(ext || '');
                            
                            return (
                              <a key={idx} href={doc} target="_blank" rel="noreferrer" className="btn btn-sm btn-light-primary">
                                <i className={`bi ${isImage ? 'bi-image' : 'bi-file-earmark'} me-1`}></i>
                                {fileName}
                              </a>
                            );
                          })}
                        </div>
                      </div>
                    ) : null;
                  })()}
                   <div className="row mb-7">
                    <label className="col-lg-4 fw-semibold text-muted">Joined Date</label>
                    <div className="col-lg-8">
                      <span className="fw-bold fs-6 text-gray-800">{formatDateTime(user.created_at)}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Wallet Card — shown inside info tab */}
            {activeTab === 'info' && (
              <div className="card mb-5 mb-xl-10">
                <div className="card-header cursor-pointer">
                  <div className="card-title m-0">
                    <h3 className="fw-bold m-0">Wallet Balance</h3>
                  </div>
                </div>
                <div className="card-body p-9">
                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-4">
                    <div>
                      <p className="text-muted fw-semibold mb-1">Current Balance</p>
                      <span className="fs-2x fw-bold text-gray-800">
                        {user.wallet_balance ?? 0}
                      </span>
                    </div>
                    <div className="d-flex gap-3">
                      <button
                        className="btn btn-success"
                        onClick={() => openWalletModal('add')}
                      >
                        <i className="fas fa-plus me-2"></i>Add Balance
                      </button>
                      <button
                        className="btn btn-danger"
                        onClick={() => openWalletModal('deduct')}
                        disabled={(user.wallet_balance ?? 0) <= 0}
                      >
                        <i className="fas fa-minus me-2"></i>Deduct Balance
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'violations' && (
              <div className="card mb-5 mb-xl-10">
                <div className="card-header cursor-pointer">
                  <div className="card-title m-0">
                    <h3 className="fw-bold m-0">Violations</h3>
                  </div>
                  <div className="card-toolbar">
                    <button className="btn btn-danger btn-sm" onClick={openViolationModal}>
                      <i className="bi bi-exclamation-triangle-fill me-2"></i>Give Violation
                    </button>
                  </div>
                </div>
                <div className="card-body p-9">
                  {isViolationsLoading ? (
                    <PageLoader />
                  ) : violationsData?.data && violationsData.data.length > 0 ? (
                    <div className="table-responsive">
                      <table className="table align-middle table-row-dashed fs-6 gy-5">
                        <thead>
                          <tr className="text-start text-gray-400 fw-bold fs-7 text-uppercase gs-0">
                            <th>TYPE OF VIOLATION</th>
                            <th>AFFECTED CONTENT</th>
                            <th>DATE</th>
                            <th>STATUS</th>
                          </tr>
                        </thead>
                        <tbody className="text-gray-600 fw-semibold">
                          {violationsData.data.map((v: any) => (
                            <tr key={v.id}>
                              <td>{v.violation_type || 'Policy Non-Compliance'}</td>
                              <td>
                                {v.ad?.title
                                  ? `${v.ad.title} / ${v.ad.ad_category?.title || ''}`
                                  : v.ad_id}
                              </td>
                              <td>{formatDateOnly(v.created_at)}</td>
                              <td>
                                <span className={`badge ${
                                  v.status === 'Restricted' ? 'badge-light-danger' :
                                  v.status === 'Warning Issued' ? 'badge-light-warning' :
                                  'badge-light-info'
                                }`}>
                                  {v.status}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center text-muted py-10">No violations found for this user.</div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'reports' && (
              <div className="card mb-5 mb-xl-10">
                <div className="card-header cursor-pointer">
                  <div className="card-title m-0">
                    <h3 className="fw-bold m-0">Reports Against This User</h3>
                  </div>
                  {reportsData?.data && reportsData.data.length > 0 && (
                    <div className="card-toolbar">
                      <span className="badge badge-light-danger fs-7 fw-bold">
                        {reportsData.data.length} report{reportsData.data.length > 1 ? 's' : ''}
                      </span>
                    </div>
                  )}
                </div>
                <div className="card-body p-9">
                  {isReportsLoading ? (
                    <PageLoader />
                  ) : reportsData?.data && reportsData.data.length > 0 ? (
                    <div className="d-flex flex-column gap-5">
                      {reportsData.data.map((r: any) => (
                        <div key={r.id} className="border border-gray-200 rounded p-5">
                          <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-4">
                            <div className="d-flex align-items-center">
                              <div className="symbol symbol-45px me-3">
                                {r.reporter?.picture ? (
                                  <Image src={r.reporter.picture} alt={r.reporter?.name} type="avatar" className="w-45px h-45px rounded-circle object-fit-cover" />
                                ) : (
                                  <span className="symbol-label bg-light-danger text-danger fw-bold">
                                    {(r.reporter?.name || 'U').charAt(0).toUpperCase()}
                                  </span>
                                )}
                              </div>
                              <div className="d-flex flex-column">
                                <span className="fw-bold text-gray-800 fs-6">
                                  {r.reporter?.name || 'Unknown User'}
                                </span>
                                {r.reporter?.email && (
                                  <span className="text-muted fs-7">{r.reporter.email}</span>
                                )}
                              </div>
                            </div>
                            <div className="d-flex flex-column align-items-end gap-1">
                              <span className={`badge fs-8 fw-bold text-capitalize ${
                                r.status === 'resolved' ? 'badge-light-success' :
                                r.status === 'reviewed' ? 'badge-light-info' :
                                r.status === 'dismissed' ? 'badge-light-secondary text-dark' :
                                'badge-light-warning'
                              }`}>
                                {r.status || 'pending'}
                              </span>
                              <span className="text-muted fs-8">{formatDateTime(r.created_at)}</span>
                            </div>
                          </div>

                          <div className="mb-3">
                            <span className="text-muted fw-semibold fs-7 d-block mb-1">Reason</span>
                            <span className="fw-bold text-gray-800 fs-6">{r.reason || '—'}</span>
                          </div>

                          {r.details && (
                            <div className="mb-3">
                              <span className="text-muted fw-semibold fs-7 d-block mb-1">Details</span>
                              <span className="text-gray-700 fs-6">{r.details}</span>
                            </div>
                          )}

                          {Array.isArray(r.images) && r.images.length > 0 && (
                            <div>
                              <span className="text-muted fw-semibold fs-7 d-block mb-2">Attachments</span>
                              <div className="d-flex flex-wrap gap-2">
                                {r.images.map((img: string, idx: number) => (
                                  <a key={idx} href={img} target="_blank" rel="noreferrer" className="d-block">
                                    <Image src={img} alt={`Report image ${idx + 1}`} className="w-80px h-80px rounded object-fit-cover border border-gray-200" />
                                  </a>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center text-muted py-10">
                      <i className="bi bi-flag fs-3x text-gray-300 d-block mb-3"></i>
                      No reports have been filed against this user.
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'ads' && (
              <div className="card mb-5 mb-xl-10">
                 <div className="card-header cursor-pointer">
                  <div className="card-title m-0">
                    <h3 className="fw-bold m-0">User Ads</h3>
                  </div>
                </div>
                <div className="card-body p-9">
                  {isAdsLoading ? (
                    <PageLoader />
                  ) : adsData?.data?.data && adsData.data.data.length > 0 ? (
                    <div className="table-responsive">
                       <table className="table align-middle table-row-dashed fs-6 gy-5">
                        <thead>
                          <tr className="text-start text-gray-400 fw-bold fs-7 text-uppercase gs-0">
                            <th>AD ID</th>
                            <th>TITLE</th>
                            <th>CATEGORY</th>
                            <th>TYPE</th>
                            <th>LANGUAGE</th>
                            <th>STATUS</th>
                            <th>VIEWS</th>
                            <th>CLICKS</th>
                            <th>PAID AMOUNT</th>
                            <th>CREATED DATE</th>
                            <th>ACTIONS</th>
                          </tr>
                        </thead>
                        <tbody className="text-gray-600 fw-semibold">
                          {adsData.data.data.map((ad: any) => (
                            <tr key={ad.id}>
                              <td>
                                <span className="text-gray-800 fw-bold">{ad.ad_id || ad.id}</span>
                              </td>
                              <td>
                                <div className="d-flex flex-column">
                                  <span className="text-gray-800 fw-bold">{ad.title || 'N/A'}</span>
                                  {ad.description && (
                                    <span className="text-muted fs-7" style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {ad.description}
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td>
                                <span className="badge badge-light-primary">
                                  {ad.ad_category?.title || 'N/A'}
                                </span>
                              </td>
                              <td>
                                <span className="badge badge-light-info">
                                  {ad.ad_type?.title || 'N/A'}
                                </span>
                              </td>
                             
                              <td>
                                <span className="badge badge-light-success">
                                  {ad.ad_language?.toUpperCase() || 'EN'}
                                </span>
                              </td>
                              <td>
                                <span className={`badge ${getStatusColor(ad.status)}`}>
                                  {ad.status || 'N/A'}
                                </span>
                              </td>
                              <td>
                                <span className="text-gray-800 fw-bold">
                                  {ad.ad_stat?.views ?? ad.views ?? 0}
                                </span>
                              </td>
                              <td>
                                <span className="text-gray-800 fw-bold">
                                  {ad.ad_stat?.clicks ?? ad.clicks ?? 0}
                                </span>
                              </td>
                              <td>
                                <span className="text-gray-800 fw-bold">
                                  {formatCurrency(ad.payment_amount)}
                                </span>
                              </td>
                              <td>{formatDateTime(ad.created_at)}</td>
                              <td className="text-center">
                                <ActionButtons
                                  onView={() => navigate(`/admin/ads/${ad.id}`)}
                                  // onDelete={() => handleDelete(ad.id)}
                                  viewTitle="View Details"
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                       </table>
                    </div>
                  ) : (
                    <div className="text-center text-muted">No ads found for this user.</div>
                  )}
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>

    {/* Verification Modal */}
    {modalOpen && (
      <>
        <div className="modal fade show d-block" tabIndex={-1} role="dialog" style={{ zIndex: 1055 }}>
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {pendingStatus === 'approve' ? 'Approve Badge' : 'Reject Badge'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setModalOpen(false)}
                  disabled={isUpdating}
                />
              </div>
              <div className="modal-body">
                <div className="mb-4">
                  <label className="form-label fw-semibold required">Note</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    placeholder={`Add a note for this ${pendingStatus === 'approve' ? 'approval' : 'rejection'}...`}
                    value={node}
                    onChange={(e) => setNode(e.target.value)}
                  />
                </div>

                {pendingStatus === 'approve' && (
                  <div className="mb-2">
                    <label className="form-label fw-semibold">Upload Documents</label>
                    <input
                      type="file"
                      className="form-control"
                      multiple
                      accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
                      onChange={(e) => setDocuments(e.target.files ? Array.from(e.target.files) : [])}
                    />
                    {documents.length > 0 && (
                      <div className="mt-2">
                        {documents.map((f, i) => (
                          <span key={i} className="badge badge-light-primary me-1 mb-1">{f.name}</span>
                        ))}
                      </div>
                    )}
                    <div className="text-muted fs-7 mt-1">Accepted: JPG, PNG, PDF, DOC, DOCX (max 5MB each)</div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light"
                  onClick={() => setModalOpen(false)}
                  disabled={isUpdating}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={`btn ${pendingStatus === 'approve' ? 'btn-success' : 'btn-danger'}`}
                  onClick={handleVerificationSubmit}
                  disabled={isUpdating}
                >
                  {isUpdating ? (
                    <span className="indicator-progress d-block">
                      Please wait... <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                    </span>
                  ) : pendingStatus === 'approve' ? 'Approve' : 'Reject'}
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="modal-backdrop fade show" style={{ zIndex: 1054 }} onClick={() => !isUpdating && setModalOpen(false)} />
      </>
    )}

    {/* Wallet Modal */}
    {walletModalOpen && (
      <>
        <div className="modal fade show d-block" tabIndex={-1} role="dialog" style={{ zIndex: 1055 }}>
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {walletType === 'add' ? 'Add Wallet Balance' : 'Deduct Wallet Balance'}
                  <span className="text-muted fs-6 ms-2">— {user.name}</span>
                </h5>
                <button type="button" className="btn-close" onClick={() => setWalletModalOpen(false)} />
              </div>
              <div className="modal-body">
                <div className="d-flex gap-3 mb-5">
                  <button
                    type="button"
                    className={`btn flex-grow-1 ${walletType === 'add' ? 'btn-success' : 'btn-light'}`}
                    onClick={() => setWalletType('add')}
                  >
                    <i className="fas fa-plus me-2"></i>Add
                  </button>
                  <button
                    type="button"
                    className={`btn flex-grow-1 ${walletType === 'deduct' ? 'btn-danger' : 'btn-light'}`}
                    onClick={() => setWalletType('deduct')}
                  >
                    <i className="fas fa-minus me-2"></i>Deduct
                  </button>
                </div>
                <div className="mb-1 p-4 bg-light rounded mb-4">
                  <span className="text-muted fs-7">Current Balance</span>
                  <div className="fw-bold fs-4 text-gray-800">{user.wallet_balance ?? 0}</div>
                </div>
                <div className="mb-4">
                  <label className="form-label required">Amount</label>
                  <input
                    type="number"
                    className="form-control form-control-solid"
                    placeholder="Enter amount"
                    min="0.01"
                    step="0.01"
                    value={walletAmount}
                    onChange={(e) => setWalletAmount(e.target.value)}
                    autoFocus
                  />
                </div>
                <div className="mb-2">
                  <label className="form-label">Note (optional)</label>
                  <input
                    type="text"
                    className="form-control form-control-solid"
                    placeholder="e.g. Refund, penalty, bonus…"
                    value={walletNote}
                    onChange={(e) => setWalletNote(e.target.value)}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-light" onClick={() => setWalletModalOpen(false)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className={`btn ${walletType === 'add' ? 'btn-success' : 'btn-danger'}`}
                  onClick={handleWalletSubmit}
                  disabled={isWalletLoading || !walletAmount}
                >
                  {isWalletLoading ? (
                    <><span className="spinner-border spinner-border-sm me-2"></span>Processing…</>
                  ) : walletType === 'add' ? 'Add Balance' : 'Deduct Balance'}
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="modal-backdrop fade show" style={{ zIndex: 1054 }} onClick={() => !isWalletLoading && setWalletModalOpen(false)} />
      </>
    )}

    {/* Violation Modal */}
    {violationModalOpen && (
      <>
        <div className="modal fade show d-block" tabIndex={-1} role="dialog" style={{ zIndex: 1055 }}>
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Give Violation<span className="text-muted fs-6 ms-2">— {user.name}</span></h5>
                <button type="button" className="btn-close" onClick={() => setViolationModalOpen(false)} disabled={isViolationSubmitting} />
              </div>
              <div className="modal-body">
                <div className="mb-4">
                  <label className="form-label fw-semibold required">Affected Ad</label>
                  <select
                    className="form-select form-select-solid"
                    value={violationAdId}
                    onChange={(e) => setViolationAdId(e.target.value)}
                  >
                    <option value="">Select an ad…</option>
                    {adsData?.data?.data?.map((ad: any) => (
                      <option key={ad.id} value={ad.id}>
                        {ad.title || `Ad #${ad.ad_id || ad.id}`} {ad.ad_category?.title ? `/ ${ad.ad_category.title}` : ''}
                      </option>
                    ))}
                  </select>
                  {(!adsData?.data?.data || adsData.data.data.length === 0) && (
                    <div className="text-muted fs-7 mt-1">No ads found. Switch to the Ads tab to load them first.</div>
                  )}
                </div>
                <div className="mb-4">
                  <label className="form-label fw-semibold required">Type of Violation</label>
                  <input
                    type="text"
                    className="form-control form-control-solid"
                    value={violationType}
                    onChange={(e) => setViolationType(e.target.value)}
                    placeholder="e.g. Policy Non-Compliance"
                  />
                </div>
                <div className="mb-4">
                  <label className="form-label fw-semibold required">Status</label>
                  <select
                    className="form-select form-select-solid"
                    value={violationStatus}
                    onChange={(e) => setViolationStatus(e.target.value)}
                  >
                    <option value="Under Review">Under Review</option>
                    <option value="Warning Issued">Warning Issued</option>
                    <option value="Restricted">Restricted</option>
                  </select>
                </div>
                <div className="mb-2">
                  <label className="form-label">Note (optional)</label>
                  <textarea
                    className="form-control form-control-solid"
                    rows={3}
                    placeholder="Additional details about this violation…"
                    value={violationNote}
                    onChange={(e) => setViolationNote(e.target.value)}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-light" onClick={() => setViolationModalOpen(false)} disabled={isViolationSubmitting}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleViolationSubmit}
                  disabled={isViolationSubmitting || !violationAdId}
                >
                  {isViolationSubmitting ? (
                    <><span className="spinner-border spinner-border-sm me-2"></span>Submitting…</>
                  ) : 'Give Violation'}
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="modal-backdrop fade show" style={{ zIndex: 1054 }} onClick={() => !isViolationSubmitting && setViolationModalOpen(false)} />
      </>
    )}
    </>
  );
};

export default AdminUserView;


